import { SxProps } from '@mui/material'
import { Box, Theme } from '@mui/system'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

const collapseDurationMs = 220

type AnimatedScheduleCollapseProps = {
  open: boolean
  children: React.ReactNode
  sx?: SxProps<Theme>
}

const AnimatedScheduleCollapse = ({ open, children, sx }: AnimatedScheduleCollapseProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [renderChildren, setRenderChildren] = useState(open)
  const [height, setHeight] = useState<number | undefined>(open ? undefined : 0)

  useEffect(() => {
    if (open) {
      setRenderChildren(true)
      return
    }
    if (typeof window === 'undefined') {
      setRenderChildren(false)
      return
    }
    const timeout = window.setTimeout(() => setRenderChildren(false), collapseDurationMs)
    return () => window.clearTimeout(timeout)
  }, [open])

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const node = contentRef.current
    if (!node) {
      return
    }
    const contentHeight = node.scrollHeight
    if (open) {
      setHeight(contentHeight)
      const timeout = window.setTimeout(() => {
        setHeight(undefined)
      }, collapseDurationMs)
      return () => window.clearTimeout(timeout)
    }
    setHeight(contentHeight)
    const raf = window.requestAnimationFrame(() => setHeight(0))
    return () => window.cancelAnimationFrame(raf)
  }, [open, renderChildren])

  if (!renderChildren && !open) {
    return null
  }

  return (
    <Box
      sx={{
        height: height === undefined ? 'auto' : `${height}px`,
        overflow: 'hidden',
        opacity: open ? 1 : 0,
        transition: `height ${collapseDurationMs}ms ease, opacity ${collapseDurationMs}ms ease`,
        ...sx,
      }}
    >
      <Box ref={contentRef}>{renderChildren ? children : null}</Box>
    </Box>
  )
}

export default AnimatedScheduleCollapse
